import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  listTeacherPolicies,
  setTeacherPolicy,
  listMaterialAccessLogs,
  getTeacherMaterialDetail,
  grantExtraAccess,
  resetMaterialStats
} from '../../api/materialAccessPolicyApi';
import { getMyProfile } from '../../api/userProfileApi';
import { listCampuses } from '../../api/campusPortalApi';
import { hasMaterialAccessPage } from '../../utils/materialAccessCampus';
import * as S from './Management.styles';

/**
 * Material Access Audit
 *
 * Tab 1 "Teachers"  - who may open digital materials (director/manager switch)
 * Tab 2 "View Log"  - who opened which material and when. Two modes: the
 *                     quota ledger (2-hour window applied) and the raw click
 *                     log, which counts every single open
 * Tab 3 "Remaining" - views/prints left per teacher and material
 *
 * "Reset Stats" (director/manager) restarts a campus's displayed statistics
 * from now, typically at term start. Nothing is deleted and quota is not
 * refilled; "Show all history" brings the earlier numbers back.
 *
 * Reading is open to portal_admin and above; the switch itself is limited to
 * directors and managers. Both are campus-scoped by the backend, so nobody
 * sees a teacher or a course group they could not already see.
 *
 * The page itself is only open to campuses with CampusPortal.materialAccess
 * switched on (system admins and developers always get it).
 */
const MaterialAccessAudit = () => {
  const auth = useAuth();

  const userGroups = useMemo(
    () => auth.user?.profile['cognito:groups'] || [],
    [auth.user]
  );
  const portalGroups = useMemo(
    () => userGroups.filter(g => g.startsWith('portal_')),
    [userGroups]
  );

  const isDeveloper = portalGroups.includes('portal_developer');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isDirector = portalGroups.includes('portal_director');
  const isManager = portalGroups.includes('portal_manager');
  const isAdmin = portalGroups.includes('portal_admin');

  const hasViewRole = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const isGlobalAdmin = isDeveloper || isSystemAdmin;
  const canManage = isDeveloper || isSystemAdmin || isDirector || isManager;

  // null = still checking the user's campuses
  const [campusEnabled, setCampusEnabled] = useState(null);
  const canView = hasViewRole && campusEnabled === true;

  const [activeTab, setActiveTab] = useState('teachers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [records, setRecords] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [summary, setSummary] = useState([]);
  const [usage, setUsage] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalClicks, setTotalClicks] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [clicksTruncated, setClicksTruncated] = useState(false);
  // 'quota' = the 3-views ledger, 'raw' = every single open
  const [logMode, setLogMode] = useState('quota');
  const [campusRestricted, setCampusRestricted] = useState(true);
  // Per campus: when its stats were last reset ([] / resetAt '' = never)
  const [statsResets, setStatsResets] = useState([]);
  const [includeHistory, setIncludeHistory] = useState(false);

  // Reset stats modal
  const [resetOpen, setResetOpen] = useState(false);
  const [resetCampusIds, setResetCampusIds] = useState([]);
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState(null);

  // Filters for the log / remaining tabs
  const [teacherFilter, setTeacherFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Block/unblock modal
  const [policyTarget, setPolicyTarget] = useState(null);
  const [policyReason, setPolicyReason] = useState('');

  // Teacher material detail modal (click a teacher row)
  const [detailTeacher, setDetailTeacher] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Grant extra access modal (opened from the detail modal or the Remaining tab)
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantOperationType, setGrantOperationType] = useState('VIEW');
  const [grantAmount, setGrantAmount] = useState(1);
  const [grantReason, setGrantReason] = useState('');
  const [grantSaving, setGrantSaving] = useState(false);
  const [grantError, setGrantError] = useState(null);

  // Column sort state, one per table ({ key, dir } or null = original order)
  const [teachersSort, setTeachersSort] = useState(null);
  const [quotaSort, setQuotaSort] = useState(null);
  const [rawSort, setRawSort] = useState(null);
  const [remainingSort, setRemainingSort] = useState(null);

  const makeSortHandler = (setSort) => (key) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears back to original order
    });
  };
  const handleTeachersSort = makeSortHandler(setTeachersSort);
  const handleQuotaSort = makeSortHandler(setQuotaSort);
  const handleRawSort = makeSortHandler(setRawSort);
  const handleRemainingSort = makeSortHandler(setRemainingSort);

  const applySort = (items, sortConfig, accessor) => {
    if (!sortConfig) return items;
    const { key, dir } = sortConfig;
    const sorted = [...items].sort((a, b) => {
      const va = accessor(a, key);
      const vb = accessor(b, key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb));
    });
    return dir === 'desc' ? sorted.reverse() : sorted;
  };

  const SortableTh = ({ label, sortKey, sortConfig, onSort }) => {
    const active = sortConfig?.key === sortKey;
    const arrow = active ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : '';
    return (
      <S.Th
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => onSort(sortKey)}
      >
        {label}{arrow}
      </S.Th>
    );
  };

  useEffect(() => {
    if (auth.isLoading) return;
    if (!hasViewRole) {
      setCampusEnabled(false);
      return;
    }
    if (isGlobalAdmin) {
      setCampusEnabled(true);
      return;
    }
    let cancelled = false;
    Promise.all([getMyProfile(), listCampuses()])
      .then(([profile, campuses]) => {
        const userCampusIds = profile?.campusIds?.length
          ? profile.campusIds
          : (profile?.campusId ? [profile.campusId] : []);
        if (!cancelled) setCampusEnabled(hasMaterialAccessPage(campuses, { userCampusIds }));
      })
      .catch(() => {
        if (!cancelled) setCampusEnabled(false);
      });
    return () => { cancelled = true; };
  }, [auth.isLoading, hasViewRole, isGlobalAdmin]);

  const fetchData = useCallback(async () => {
    if (campusEnabled === null) return;
    if (!canView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [teacherResult, logResult] = await Promise.all([
        listTeacherPolicies({ includeHistory }),
        listMaterialAccessLogs({
          userId: teacherFilter || undefined,
          startDate: dateFrom || undefined,
          endDate: dateTo || undefined,
          includeHistory
        })
      ]);
      setTeachers(teacherResult.teachers);
      setStatsResets(teacherResult.statsResets);
      setCampusRestricted(teacherResult.campusRestricted);
      setRecords(logResult.records);
      setClicks(logResult.clicks);
      setSummary(logResult.summary);
      setUsage(logResult.usage);
      setTotalRecords(logResult.totalRecords);
      setTotalClicks(logResult.totalClicks);
      setTruncated(logResult.truncated);
      setClicksTruncated(logResult.clicksTruncated);
    } catch (err) {
      console.error('Error loading material access data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campusEnabled, canView, teacherFilter, dateFrom, dateTo, includeHistory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openPolicyModal = (teacher) => {
    setPolicyTarget(teacher);
    setPolicyReason(teacher.reason || '');
  };

  const closePolicyModal = () => {
    setPolicyTarget(null);
    setPolicyReason('');
  };

  const handleTogglePolicy = async () => {
    if (!policyTarget) return;
    setSaving(true);
    setError(null);
    try {
      const nextBlocked = !policyTarget.blocked;
      await setTeacherPolicy(policyTarget.userId, nextBlocked, policyReason);
      closePolicyModal();
      await fetchData();
    } catch (err) {
      console.error('Error updating policy:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openResetModal = () => {
    // A single-campus manager has nothing to choose, so tick it for them
    setResetCampusIds(statsResets.length === 1 ? [statsResets[0].campusId] : []);
    setResetError(null);
    setResetOpen(true);
  };

  const closeResetModal = () => {
    setResetOpen(false);
    setResetCampusIds([]);
    setResetError(null);
  };

  const toggleResetCampus = (campusId) => {
    setResetCampusIds(prev => (prev.includes(campusId)
      ? prev.filter(id => id !== campusId)
      : [...prev, campusId]));
  };

  const handleResetStats = async () => {
    if (resetCampusIds.length === 0) return;
    setResetSaving(true);
    setResetError(null);
    try {
      await resetMaterialStats(resetCampusIds);
      closeResetModal();
      if (includeHistory) {
        setIncludeHistory(false); // refetches through fetchData's dependency
      } else {
        await fetchData();
      }
    } catch (err) {
      console.error('Error resetting stats:', err);
      setResetError(err.message);
    } finally {
      setResetSaving(false);
    }
  };

  const openTeacherDetail = async (teacher) => {
    setDetailTeacher(teacher);
    setDetailData(null);
    setDetailError(null);
    setExpandedRows(new Set());
    setDetailLoading(true);
    try {
      const result = await getTeacherMaterialDetail(teacher.userId);
      setDetailData(result);
    } catch (err) {
      console.error('Error loading teacher material detail:', err);
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTeacherDetail = () => {
    setDetailTeacher(null);
    setDetailData(null);
    setDetailError(null);
  };

  const toggleExpandRow = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openGrantModal = (target) => {
    setGrantTarget(target);
    setGrantOperationType('VIEW');
    setGrantAmount(1);
    setGrantReason('');
    setGrantError(null);
  };

  const closeGrantModal = () => {
    setGrantTarget(null);
    setGrantError(null);
  };

  const handleSaveGrant = async () => {
    if (!grantTarget) return;
    const amount = parseInt(grantAmount, 10);
    if (!amount || amount <= 0) {
      setGrantError('Enter a positive number of extra uses');
      return;
    }
    setGrantSaving(true);
    setGrantError(null);
    try {
      await grantExtraAccess(
        grantTarget.userId,
        grantTarget.materialId,
        grantTarget.courseGroupId,
        grantOperationType,
        amount,
        grantReason.trim()
      );
      closeGrantModal();
      // Refresh whichever view triggered the grant
      if (detailTeacher) {
        const result = await getTeacherMaterialDetail(detailTeacher.userId);
        setDetailData(result);
      }
      await fetchData();
    } catch (err) {
      console.error('Error granting extra access:', err);
      setGrantError(err.message);
    } finally {
      setGrantSaving(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-AU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const searchLower = search.trim().toLowerCase();

  const filteredRecords = useMemo(() => {
    if (!searchLower) return records;
    return records.filter(r =>
      (r.userName || '').toLowerCase().includes(searchLower) ||
      (r.materialName || '').toLowerCase().includes(searchLower) ||
      (r.courseGroupTitle || '').toLowerCase().includes(searchLower)
    );
  }, [records, searchLower]);

  const filteredClicks = useMemo(() => {
    if (!searchLower) return clicks;
    return clicks.filter(c =>
      (c.userName || '').toLowerCase().includes(searchLower) ||
      (c.materialName || '').toLowerCase().includes(searchLower) ||
      (c.courseGroupTitle || '').toLowerCase().includes(searchLower)
    );
  }, [clicks, searchLower]);

  const filteredUsage = useMemo(() => {
    if (!searchLower) return usage;
    return usage.filter(u =>
      (u.userName || '').toLowerCase().includes(searchLower) ||
      (u.materialName || '').toLowerCase().includes(searchLower) ||
      (u.courseGroupTitle || '').toLowerCase().includes(searchLower)
    );
  }, [usage, searchLower]);

  const filteredTeachers = useMemo(() => {
    if (!searchLower) return teachers;
    return teachers.filter(t =>
      (t.name || '').toLowerCase().includes(searchLower) ||
      (t.email || '').toLowerCase().includes(searchLower)
    );
  }, [teachers, searchLower]);

  const teacherSortAccessor = (t, key) => {
    switch (key) {
      case 'name': return (t.name || '').toLowerCase();
      case 'campus': return (t.campusNames || []).join(', ').toLowerCase();
      case 'status': return t.blocked ? 1 : 0;
      case 'views': return t.viewCount || 0;
      case 'opens': return t.clickCount || 0;
      case 'materialsOpened': return t.materialCount || 0;
      case 'lastAccess': return t.lastAccessAt ? new Date(t.lastAccessAt).getTime() : null;
      case 'changedBy': return (t.updatedByName || '').toLowerCase();
      default: return null;
    }
  };
  const rowSortAccessor = (r, key) => {
    switch (key) {
      case 'date': return r.createdAt ? new Date(r.createdAt).getTime() : null;
      case 'who': return (r.userName || '').toLowerCase();
      case 'role': return (r.role || '').toLowerCase();
      case 'material': return (r.materialName || '').toLowerCase();
      case 'courseGroup': return (r.courseGroupTitle || '').toLowerCase();
      case 'campus': return (r.campusName || '').toLowerCase();
      case 'action': return r.operationType || '';
      case 'charged': return r.counted ? 1 : 0;
      default: return null;
    }
  };
  const usageSortAccessor = (u, key) => {
    switch (key) {
      case 'teacher': return (u.userName || '').toLowerCase();
      case 'material': return (u.materialName || '').toLowerCase();
      case 'courseGroup': return (u.courseGroupTitle || '').toLowerCase();
      case 'viewsLeft': return u.viewRemaining;
      case 'printsLeft': return u.printRemaining;
      case 'lastAccess': return u.lastAccessAt ? new Date(u.lastAccessAt).getTime() : null;
      default: return null;
    }
  };

  const sortedTeachers = useMemo(
    () => applySort(filteredTeachers, teachersSort, teacherSortAccessor),
    [filteredTeachers, teachersSort]
  );
  const sortedRecords = useMemo(
    () => applySort(filteredRecords, quotaSort, rowSortAccessor),
    [filteredRecords, quotaSort]
  );
  const sortedClicks = useMemo(
    () => applySort(filteredClicks, rawSort, rowSortAccessor),
    [filteredClicks, rawSort]
  );
  const sortedUsage = useMemo(
    () => applySort(filteredUsage, remainingSort, usageSortAccessor),
    [filteredUsage, remainingSort]
  );

  const blockedCount = teachers.filter(t => t.blocked).length;
  const resetCampuses = statsResets.filter(r => r.resetAt);
  const activeTeacherCount = summary.length;
  const distinctMaterials = useMemo(
    () => new Set(records.map(r => r.materialId)).size,
    [records]
  );

  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Material Access</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  if (!canView) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Material Access</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          {hasViewRole
            ? 'Material Access is not enabled.'
            : 'You do not have permission to view material access records. This page is available to Admins, Managers, Directors and System Administrators.'}
        </S.ErrorBanner>
      </S.Container>
    );
  }

  const renderTeachersTab = () => (
    <>
      <S.Note style={{ marginTop: 0, marginBottom: '12px' }}>
        <div style={{ fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
          How to read these numbers
        </div>
        <div>
          <strong>Views</strong> - how many of the teacher&apos;s view allowance
          they have used. Each teacher can view each material <strong>3 times</strong>.
          The first time they open a material, one view is used and a 2-hour
          window starts. Opening the same material again inside that window is
          free. The first open after the window ends uses the next view and
          starts a new window. The window always runs from the open that used
          the view - opening it again in between does not extend it.
        </div>
        <div style={{ marginTop: '6px' }}>
          <strong>Opens</strong> - every single time the teacher opened a PDF,
          including the first open, free re-opens inside the 2-hour window, and
          answer sheets. Opens is therefore always equal to or higher than Views.
        </div>
        <div style={{ marginTop: '6px' }}>
          <strong>Materials Opened</strong> - how many different materials the
          teacher has used at least one view or print on. Opening the same
          material many times still counts as 1.
        </div>
        <div style={{ marginTop: '6px' }}>
          Not counted in Views: answer sheets (unlimited and free) and prints
          (prints have their own allowance of 3 per material).
        </div>
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', color: '#374151', fontWeight: 600 }}>
            Example - one teacher opens the same material four times
          </summary>
          <table style={{ marginTop: '8px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Time', 'What happens', 'Views', 'Opens'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '4px 16px 4px 0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['9:00', 'First open - uses 1 view, window runs to 11:00', '1', '1'],
                ['9:30', 'Re-open inside the window - free', '1', '2'],
                ['10:50', 'Re-open, still before 11:00 - free', '1', '3'],
                ['11:05', 'Window has ended - uses the 2nd view, new window to 13:05', '2', '4']
              ].map(row => (
                <tr key={row[0]}>
                  {row.map((cell, i) => (
                    <td key={i} style={{ padding: '4px 16px 4px 0' }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '6px' }}>
            Result: Views 2, Opens 4, and 1 view left on this material. When all
            3 views are used, the teacher cannot open it again unless a Director
            or Manager grants extra views (click the teacher&apos;s row).
          </div>
        </details>
      </S.Note>
      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <SortableTh label="Teacher" sortKey="name" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Campus" sortKey="campus" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Digital Materials" sortKey="status" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Views" sortKey="views" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Opens" sortKey="opens" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Materials Opened" sortKey="materialsOpened" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Last Access" sortKey="lastAccess" sortConfig={teachersSort} onSort={handleTeachersSort} />
              <SortableTh label="Changed By" sortKey="changedBy" sortConfig={teachersSort} onSort={handleTeachersSort} />
              {canManage && <S.Th>Action</S.Th>}
            </tr>
          </thead>
          <tbody>
            {sortedTeachers.length === 0 ? (
              <tr>
                <S.Td colSpan={canManage ? 9 : 8} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No teachers found
                </S.Td>
              </tr>
            ) : sortedTeachers.map(t => (
              <tr
                key={t.userId}
                style={{ cursor: 'pointer' }}
                onClick={() => openTeacherDetail(t)}
              >
                <S.Td>
                  <S.UserName>{t.name}</S.UserName>
                  <S.StudentDetail>{t.email}</S.StudentDetail>
                </S.Td>
                <S.Td>
                  {t.campusNames.length > 0
                    ? t.campusNames.join(', ')
                    : <span style={{ color: '#9ca3af' }}>Unassigned</span>}
                </S.Td>
                <S.Td>
                  <S.Badge color={t.blocked ? '#ef4444' : '#10b981'}>
                    {t.blocked ? 'Blocked' : 'Allowed'}
                  </S.Badge>
                  {t.blocked && t.reason && (
                    <S.StudentDetail style={{ marginTop: '4px' }}>{t.reason}</S.StudentDetail>
                  )}
                </S.Td>
                <S.Td>{t.viewCount}</S.Td>
                <S.Td>
                  {t.clickCount > 0
                    ? t.clickCount
                    : <span style={{ color: '#9ca3af' }}>0</span>}
                </S.Td>
                <S.Td>{t.materialCount}</S.Td>
                <S.Td>{formatDateTime(t.lastAccessAt)}</S.Td>
                <S.Td>
                  {t.updatedByName
                    ? <>{t.updatedByName}<S.StudentDetail>{formatDateTime(t.updatedAt)}</S.StudentDetail></>
                    : <span style={{ color: '#9ca3af' }}>-</span>}
                </S.Td>
                {canManage && (
                  <S.Td>
                    <S.SmallButton
                      color={t.blocked ? '#10b981' : '#ef4444'}
                      onClick={(e) => { e.stopPropagation(); openPolicyModal(t); }}
                    >
                      {t.blocked ? 'Allow' : 'Block'}
                    </S.SmallButton>
                  </S.Td>
                )}
              </tr>
            ))}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Note>
        Click a teacher to see their per-material views, prints and remaining
        quota, and to grant extra uses. Blocking a teacher
        closes every PDF inside My Course Groups for that account - course
        group materials and answers alike. It does not change which course
        groups or which materials they can see, and it does not affect the
        Materials library.
        {!canManage && ' Only a Director or Manager can change this switch.'}
      </S.Note>
    </>
  );

  const renderQuotaLog = () => (
    <>
      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <SortableTh label="Date" sortKey="date" sortConfig={quotaSort} onSort={handleQuotaSort} />
              <SortableTh label="Teacher" sortKey="who" sortConfig={quotaSort} onSort={handleQuotaSort} />
              <SortableTh label="Material" sortKey="material" sortConfig={quotaSort} onSort={handleQuotaSort} />
              <SortableTh label="Course Group" sortKey="courseGroup" sortConfig={quotaSort} onSort={handleQuotaSort} />
              <SortableTh label="Campus" sortKey="campus" sortConfig={quotaSort} onSort={handleQuotaSort} />
              <SortableTh label="Action" sortKey="action" sortConfig={quotaSort} onSort={handleQuotaSort} />
            </tr>
          </thead>
          <tbody>
            {sortedRecords.length === 0 ? (
              <tr>
                <S.Td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No records found
                </S.Td>
              </tr>
            ) : sortedRecords.map(r => (
              <tr key={r.id}>
                <S.Td>{formatDateTime(r.createdAt)}</S.Td>
                <S.Td><S.UserName>{r.userName || '-'}</S.UserName></S.Td>
                <S.Td>{r.materialName || '-'}</S.Td>
                <S.Td>{r.courseGroupTitle || <span style={{ color: '#9ca3af' }}>Deleted</span>}</S.Td>
                <S.Td>{r.campusName || '-'}</S.Td>
                <S.Td>
                  <S.Badge color={r.operationType === 'PRINT' ? '#8b5cf6' : '#3b82f6'}>
                    {r.operationType}
                  </S.Badge>
                </S.Td>
              </tr>
            ))}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Note>
        A record is written the first time a teacher opens a material; repeat
        opens within two hours reuse the same record so they do not consume an
        extra view. The counts here are therefore views charged against the
        quota, not raw clicks.
        {truncated && ` Showing the ${filteredRecords.length} most recent of ${totalRecords} records.`}
      </S.Note>
    </>
  );

  const renderRawLog = () => (
    <>
      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <SortableTh label="Date" sortKey="date" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Who" sortKey="who" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Role" sortKey="role" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="File" sortKey="material" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Course Group" sortKey="courseGroup" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Campus" sortKey="campus" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Action" sortKey="action" sortConfig={rawSort} onSort={handleRawSort} />
              <SortableTh label="Charged" sortKey="charged" sortConfig={rawSort} onSort={handleRawSort} />
            </tr>
          </thead>
          <tbody>
            {sortedClicks.length === 0 ? (
              <tr>
                <S.Td colSpan={8} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No opens recorded yet
                </S.Td>
              </tr>
            ) : sortedClicks.map(c => (
              <tr key={c.id}>
                <S.Td>{formatDateTime(c.createdAt)}</S.Td>
                <S.Td><S.UserName>{c.userName || '-'}</S.UserName></S.Td>
                <S.Td>{c.role || '-'}</S.Td>
                <S.Td>
                  {c.materialName || '-'}
                  {c.isAnswer && (
                    <S.Badge color="#8b5cf6" style={{ marginLeft: '6px' }}>Answer</S.Badge>
                  )}
                </S.Td>
                <S.Td>{c.courseGroupTitle || <span style={{ color: '#9ca3af' }}>Deleted</span>}</S.Td>
                <S.Td>{c.campusName || '-'}</S.Td>
                <S.Td>
                  <S.Badge color={c.operationType === 'PRINT' ? '#8b5cf6' : '#3b82f6'}>
                    {c.operationType}
                  </S.Badge>
                </S.Td>
                <S.Td>
                  <S.Badge color={c.counted ? '#F1AF59' : '#9ca3af'}>
                    {c.counted ? 'Charged' : 'Free'}
                  </S.Badge>
                </S.Td>
              </tr>
            ))}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Note>
        One row for every single open, with nothing filtered out - repeats
        inside the two-hour window, answer sheets, and opens by admins are all
        here. &quot;Charged&quot; marks the opens that also consumed one of the
        teacher&apos;s three views. This log starts from the day it was switched on
        (18 Aug 2026); anything before that only exists in the quota ledger.
        {clicksTruncated && ` Showing the ${filteredClicks.length} most recent of ${totalClicks} opens.`}
      </S.Note>
    </>
  );

  const renderLogTab = () => (
    <>
      <S.ButtonGroup style={{ marginBottom: '16px' }}>
        <S.Button
          color={logMode === 'quota' ? '#3b82f6' : '#9ca3af'}
          onClick={() => setLogMode('quota')}
        >
          Charged Views ({totalRecords})
        </S.Button>
        <S.Button
          color={logMode === 'raw' ? '#3b82f6' : '#9ca3af'}
          onClick={() => setLogMode('raw')}
        >
          Every Open ({totalClicks})
        </S.Button>
      </S.ButtonGroup>
      {logMode === 'quota' ? renderQuotaLog() : renderRawLog()}
    </>
  );

  const renderRemainingTab = () => (
    <>
      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <SortableTh label="Teacher" sortKey="teacher" sortConfig={remainingSort} onSort={handleRemainingSort} />
              <SortableTh label="Material" sortKey="material" sortConfig={remainingSort} onSort={handleRemainingSort} />
              <SortableTh label="Course Group" sortKey="courseGroup" sortConfig={remainingSort} onSort={handleRemainingSort} />
              <SortableTh label="Views Left" sortKey="viewsLeft" sortConfig={remainingSort} onSort={handleRemainingSort} />
              <SortableTh label="Prints Left" sortKey="printsLeft" sortConfig={remainingSort} onSort={handleRemainingSort} />
              <SortableTh label="Last Access" sortKey="lastAccess" sortConfig={remainingSort} onSort={handleRemainingSort} />
              {canManage && <S.Th>Action</S.Th>}
            </tr>
          </thead>
          <tbody>
            {sortedUsage.length === 0 ? (
              <tr>
                <S.Td colSpan={canManage ? 7 : 6} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No usage yet
                </S.Td>
              </tr>
            ) : sortedUsage.map(u => (
              <tr key={`${u.userId}-${u.materialId}-${u.courseGroupId}`}>
                <S.Td><S.UserName>{u.userName || '-'}</S.UserName></S.Td>
                <S.Td>{u.materialName || '-'}</S.Td>
                <S.Td>{u.courseGroupTitle || <span style={{ color: '#9ca3af' }}>Deleted</span>}</S.Td>
                <S.Td>
                  <S.Badge color={u.viewRemaining === 0 ? '#ef4444' : '#10b981'}>
                    {u.viewRemaining} / {u.viewLimit}
                  </S.Badge>
                </S.Td>
                <S.Td>
                  <S.Badge color={u.printRemaining === 0 ? '#ef4444' : '#6b7280'}>
                    {u.printRemaining} / {u.printLimit}
                  </S.Badge>
                </S.Td>
                <S.Td>{formatDateTime(u.lastAccessAt)}</S.Td>
                {canManage && (
                  <S.Td>
                    <S.SmallButton
                      color="#3b82f6"
                      onClick={() => openGrantModal({
                        userId: u.userId,
                        userName: u.userName,
                        materialId: u.materialId,
                        materialName: u.materialName,
                        courseGroupId: u.courseGroupId,
                        courseGroupTitle: u.courseGroupTitle
                      })}
                    >
                      Grant
                    </S.SmallButton>
                  </S.Td>
                )}
              </tr>
            ))}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Note>
        Every teacher gets {3} views and {3} prints per course group material.
        Remaining counts are calculated over the full history plus anything
        granted, so changing the date filter above does not change them.
        Answers are unlimited and are not tracked.
        {!canManage && ' Only a Director or Manager can grant extra uses.'}
      </S.Note>
    </>
  );

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Material Access</S.Title>
          <S.Subtitle>
            Who opened which teaching material, and who may open them at all
          </S.Subtitle>
        </div>
      </S.Header>

      {error && <S.ErrorBanner>{error}</S.ErrorBanner>}

      <S.StatsGrid style={{ marginBottom: '24px' }}>
        <S.StatCard color="#3b82f6">
          <S.StatNumber>{totalRecords}</S.StatNumber>
          <S.StatLabel>View Records</S.StatLabel>
          <S.StatSubtext>Matching current filters</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#8b5cf6">
          <S.StatNumber>{totalClicks}</S.StatNumber>
          <S.StatLabel>Total Opens</S.StatLabel>
          <S.StatSubtext>Raw clicks, repeats included</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#F1AF59">
          <S.StatNumber>{activeTeacherCount}</S.StatNumber>
          <S.StatLabel>Teachers Active</S.StatLabel>
          <S.StatSubtext>Opened at least one material</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#10b981">
          <S.StatNumber>{distinctMaterials}</S.StatNumber>
          <S.StatLabel>Materials Opened</S.StatLabel>
          <S.StatSubtext>Distinct files</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#ef4444">
          <S.StatNumber>{blockedCount}</S.StatNumber>
          <S.StatLabel>Teachers Blocked</S.StatLabel>
          <S.StatSubtext>Digital materials off</S.StatSubtext>
        </S.StatCard>
      </S.StatsGrid>

      <S.ButtonGroup style={{ marginBottom: '16px' }}>
        <S.Button
          color={activeTab === 'teachers' ? '#F1AF59' : '#6b7280'}
          onClick={() => setActiveTab('teachers')}
        >
          Teachers
        </S.Button>
        <S.Button
          color={activeTab === 'log' ? '#F1AF59' : '#6b7280'}
          onClick={() => setActiveTab('log')}
        >
          View Log
        </S.Button>
        <S.Button
          color={activeTab === 'remaining' ? '#F1AF59' : '#6b7280'}
          onClick={() => setActiveTab('remaining')}
        >
          Remaining Views
        </S.Button>
        {canManage && (
          <S.Button color="#ef4444" onClick={openResetModal}>
            Reset Stats
          </S.Button>
        )}
      </S.ButtonGroup>

      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search teacher, material or course group..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {activeTab !== 'teachers' && (
          <>
            <S.FilterSelect
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
            >
              <option value="">All Teachers</option>
              {teachers.map(t => (
                <option key={t.userId} value={t.userId}>{t.name}</option>
              ))}
            </S.FilterSelect>
            <S.Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '150px' }}
            />
            <S.Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '150px' }}
            />
          </>
        )}
        {campusRestricted && (
          <S.Badge color="#6b7280" style={{ alignSelf: 'center', padding: '10px 16px' }}>
            Your Campus Only
          </S.Badge>
        )}
      </S.FiltersBar>

      {resetCampuses.length > 0 && (
        <S.Note style={{ marginTop: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>
            {includeHistory
              ? 'Showing all history, including before the last reset. '
              : 'Counting from the last reset: '}
            {resetCampuses.map(r => (
              `${r.campusName} ${formatDateTime(r.resetAt)}${r.resetByName ? ` (by ${r.resetByName})` : ''}`
            )).join('; ')}
            . Remaining Views always counts the full history.
          </span>
          <S.SmallButton
            color="#6b7280"
            onClick={() => setIncludeHistory(prev => !prev)}
          >
            {includeHistory ? 'Show since reset' : 'Show all history'}
          </S.SmallButton>
        </S.Note>
      )}

      {activeTab === 'teachers' && renderTeachersTab()}
      {activeTab === 'log' && renderLogTab()}
      {activeTab === 'remaining' && renderRemainingTab()}

      {resetOpen && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h2>Reset Stats</h2>
              <S.CloseButton onClick={closeResetModal}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {resetError && <S.ErrorBanner>{resetError}</S.ErrorBanner>}
              <p style={{ marginTop: 0 }}>
                Views, Opens, Materials Opened and the numbers above will count
                again from now for the campuses you tick. Use this at the start
                of each term.
              </p>
              {statsResets.map(r => (
                <label
                  key={r.campusId}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}
                >
                  <S.Checkbox
                    checked={resetCampusIds.includes(r.campusId)}
                    onChange={() => toggleResetCampus(r.campusId)}
                    disabled={resetSaving}
                  />
                  <span>
                    {r.campusName}
                    <S.StudentDetail as="span" style={{ marginLeft: '8px' }}>
                      {r.resetAt ? `last reset ${formatDateTime(r.resetAt)}` : 'never reset'}
                    </S.StudentDetail>
                  </span>
                </label>
              ))}
              <S.Note>
                Nothing is deleted - &quot;Show all history&quot; still shows the
                earlier records. Teachers do not get their views back: Remaining
                Views keeps counting the full history.
              </S.Note>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={closeResetModal} disabled={resetSaving}>
                Cancel
              </S.Button>
              <S.Button
                color="#ef4444"
                onClick={handleResetStats}
                disabled={resetSaving || resetCampusIds.length === 0}
              >
                {resetSaving ? 'Resetting...' : 'Reset Stats'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {policyTarget && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h2>{policyTarget.blocked ? 'Allow' : 'Block'} Digital Materials</h2>
              <S.CloseButton onClick={closePolicyModal}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <p style={{ marginTop: 0 }}>
                {policyTarget.blocked ? (
                  <>
                    <strong>{policyTarget.name}</strong> will be able to open
                    course group materials and answers again.
                  </>
                ) : (
                  <>
                    <strong>{policyTarget.name}</strong> will no longer be able to
                    open any course group material or answer PDF. Their course
                    groups, students and everything else stay unchanged.
                  </>
                )}
              </p>
              <S.FormGroup>
                <label>Note (optional)</label>
                <S.TextArea
                  value={policyReason}
                  onChange={(e) => setPolicyReason(e.target.value)}
                  placeholder="Why this teacher is on paper only..."
                />
              </S.FormGroup>
              <S.Note>
                Already-open PDFs stay open until their link expires (5 minutes).
                This switch stops new ones from being issued; it cannot stop
                someone photographing a screen.
              </S.Note>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={closePolicyModal} disabled={saving}>
                Cancel
              </S.Button>
              <S.Button
                color={policyTarget.blocked ? '#10b981' : '#ef4444'}
                onClick={handleTogglePolicy}
                disabled={saving}
              >
                {saving ? 'Saving...' : (policyTarget.blocked ? 'Allow Access' : 'Block Access')}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {detailTeacher && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '920px' }}>
            <S.ModalHeader>
              <h2>{detailTeacher.name}</h2>
              <S.CloseButton onClick={closeTeacherDetail}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {detailLoading && <p>Loading...</p>}
              {detailError && <S.ErrorBanner>{detailError}</S.ErrorBanner>}
              {!detailLoading && !detailError && detailData && (
                detailData.materials.length === 0 ? (
                  <S.EmptyState>
                    No materials assigned to this teacher&apos;s subjects yet.
                  </S.EmptyState>
                ) : (
                  <S.TableContainer>
                    <S.Table $cards>
                      <thead>
                        <tr>
                          <S.Th>Material</S.Th>
                          <S.Th>Course Group</S.Th>
                          <S.Th>Views</S.Th>
                          <S.Th>Prints</S.Th>
                          <S.Th>Last Access</S.Th>
                          <S.Th></S.Th>
                          {canManage && <S.Th>Action</S.Th>}
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.materials.map(m => {
                          const rowKey = `${m.materialId}-${m.courseGroupId}`;
                          const expanded = expandedRows.has(rowKey);
                          const history = [
                            ...m.accessEvents.map(e => ({ ...e, kind: 'access' })),
                            ...m.grantEvents.map(e => ({ ...e, kind: 'grant' }))
                          ].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
                          return (
                            <React.Fragment key={rowKey}>
                              <tr>
                                <S.Td>{m.materialName}</S.Td>
                                <S.Td>{m.courseGroupTitle}</S.Td>
                                <S.Td>
                                  <S.Badge color={m.viewRemaining === 0 ? '#ef4444' : '#10b981'}>
                                    {m.viewUsed} used / {m.viewRemaining} left of {m.viewLimit}
                                  </S.Badge>
                                </S.Td>
                                <S.Td>
                                  <S.Badge color={m.printRemaining === 0 ? '#ef4444' : '#6b7280'}>
                                    {m.printUsed} used / {m.printRemaining} left of {m.printLimit}
                                  </S.Badge>
                                </S.Td>
                                <S.Td>{formatDateTime(m.lastAccessAt)}</S.Td>
                                <S.Td>
                                  {history.length > 0 && (
                                    <S.SmallButton color="#6b7280" onClick={() => toggleExpandRow(rowKey)}>
                                      {expanded ? 'Hide' : `History (${history.length})`}
                                    </S.SmallButton>
                                  )}
                                </S.Td>
                                {canManage && (
                                  <S.Td>
                                    <S.SmallButton
                                      color="#3b82f6"
                                      onClick={() => openGrantModal({
                                        userId: detailTeacher.userId,
                                        userName: detailTeacher.name,
                                        materialId: m.materialId,
                                        materialName: m.materialName,
                                        courseGroupId: m.courseGroupId,
                                        courseGroupTitle: m.courseGroupTitle
                                      })}
                                    >
                                      Grant
                                    </S.SmallButton>
                                  </S.Td>
                                )}
                              </tr>
                              {expanded && (
                                <tr>
                                  <S.Td colSpan={canManage ? 7 : 6} style={{ background: '#f9fafb' }}>
                                    {history.map((h, idx) => (
                                      <div key={idx} style={{ fontSize: '13px', padding: '2px 0' }}>
                                        {formatDateTime(h.createdAt)} -{' '}
                                        {h.kind === 'access'
                                          ? <>{h.operationType}</>
                                          : <>Granted +{h.amount} {h.operationType} by {h.grantedByName}{h.reason && ` ("${h.reason}")`}</>}
                                      </div>
                                    ))}
                                  </S.Td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </S.Table>
                  </S.TableContainer>
                )
              )}
              <S.Note style={{ marginTop: '16px' }}>
                Materials shown here are matched by subject to this teacher&apos;s
                course group assignments; the week/schedule window that gates
                what they can open right now is ignored on purpose, so an
                exhausted material from a past week still shows up to manage.
              </S.Note>
            </S.ModalContent>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {grantTarget && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h2>Grant Extra Access</h2>
              <S.CloseButton onClick={closeGrantModal}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <p style={{ marginTop: 0 }}>
                <strong>{grantTarget.userName}</strong> - {grantTarget.materialName}
                <S.StudentDetail>{grantTarget.courseGroupTitle}</S.StudentDetail>
              </p>
              {grantError && <S.ErrorBanner>{grantError}</S.ErrorBanner>}
              <S.FormGroup>
                <label>Type</label>
                <S.ButtonGroup>
                  <S.Button
                    color={grantOperationType === 'VIEW' ? '#3b82f6' : '#9ca3af'}
                    onClick={() => setGrantOperationType('VIEW')}
                  >
                    Views
                  </S.Button>
                  <S.Button
                    color={grantOperationType === 'PRINT' ? '#3b82f6' : '#9ca3af'}
                    onClick={() => setGrantOperationType('PRINT')}
                  >
                    Prints
                  </S.Button>
                </S.ButtonGroup>
              </S.FormGroup>
              <S.FormGroup>
                <label>Extra uses to grant</label>
                <S.Input
                  type="number"
                  min="1"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  style={{ width: '120px' }}
                />
              </S.FormGroup>
              <S.FormGroup>
                <label>Note (optional)</label>
                <S.TextArea
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="Why this teacher needs more access..."
                />
              </S.FormGroup>
              <S.Note>
                This adds on top of the standard 3, it does not reset what
                they&apos;ve already used - their existing view/print history stays
                exactly as recorded.
              </S.Note>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={closeGrantModal} disabled={grantSaving}>
                Cancel
              </S.Button>
              <S.Button color="#3b82f6" onClick={handleSaveGrant} disabled={grantSaving}>
                {grantSaving ? 'Saving...' : 'Grant Access'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}
    </S.Container>
  );
};

export default MaterialAccessAudit;
